/**
 * User Management Types
 *
 * Type definitions for user management, including users, roles, organizations,
 * and activity timeline events.
 */

// ============================================================================
// User Status Enums
// ============================================================================

export const UserStatus = {
  Active: 'active',
  Suspended: 'suspended',
  Archived: 'archived',
} as const
export type UserStatus = typeof UserStatus[keyof typeof UserStatus]

export const InvitationStatus = {
  Pending: 'pending',
  Accepted: 'accepted',
  Redeemed: 'redeemed',
  Declined: 'declined',
  Revoked: 'revoked',
  Expired: 'expired',
} as const
export type InvitationStatus = typeof InvitationStatus[keyof typeof InvitationStatus]

type StatusVariantDescriptor = {
  label: string
  variant: 'blue' | 'indigo' | 'green' | 'gray' | 'red' | 'amber'
}

// Status badge variants with color coding
export const USER_STATUS_VARIANTS: Record<UserStatus, StatusVariantDescriptor> = {
  [UserStatus.Active]: { label: 'Active', variant: 'green' },
  [UserStatus.Suspended]: { label: 'Suspended', variant: 'gray' },
  [UserStatus.Archived]: { label: 'Archived', variant: 'red' },
}

// Inviter-facing labels (spec §8.1)
export const INVITATION_STATUS_VARIANTS: Record<InvitationStatus, StatusVariantDescriptor> = {
  [InvitationStatus.Pending]: { label: 'Waiting', variant: 'blue' },
  [InvitationStatus.Accepted]: { label: "Accepted — hasn't signed in yet", variant: 'indigo' },
  [InvitationStatus.Redeemed]: { label: 'On board', variant: 'green' },
  [InvitationStatus.Declined]: { label: 'Declined', variant: 'gray' },
  [InvitationStatus.Revoked]: { label: 'Withdrawn', variant: 'gray' },
  [InvitationStatus.Expired]: { label: 'Expired', variant: 'amber' },
}

export type UserStatusVariant = StatusVariantDescriptor

// ============================================================================
// User Role Types
// ============================================================================

export interface UserRole {
  id: string
  name: string
  description?: string
  isActive: boolean
}

// ============================================================================
// User Event Types (Timeline)
// ============================================================================

export interface UserEvent {
  eventId: string
  eventType: string
  eventDescription?: string
  timestamp: number // Unix timestamp
  metadata?: Record<string, unknown>
}

// ============================================================================
// User Types
// ============================================================================

export interface User {
  userNumber?: number // 10-digit unique user identifier
  firstName: string
  lastName: string
  nickName?: string
  emailAddress?: string
  mobileNumber?: string
  countryCode?: string
  jobTitle?: string
  profilePicture?: string // URL or file reference
  userStatus: UserStatus
  invitationStatus?: InvitationStatus
  organization?: number // Deprecated: Single organization number (kept for backward compatibility)
  organizations?: number[] // Array of organization numbers - primary field for multi-org support
  createdAt?: number // Unix timestamp
  updatedAt?: number // Unix timestamp
}

// ============================================================================
// API Request/Response Types — derived from Zod schemas (generatedSchema.ts is source of truth)
// ============================================================================

import type {
  UpsertUserRegistry,
  ResendInvitationRegistry,
  GetInvitationListRegistry,
  GetInvitationTypesRegistry,
  SetUserStatusRegistry,
  FetchUserProfilePictureRegistry,
  GetUsersRegistry,
  GetUserByNumberRegistry,
  GetUserEventTimelineRegistry,
} from '@generated-schemas'
import type { InferRequest, InferResponse } from '@/types/registryTypes'

export type UpsertUserRequest                = InferRequest<typeof UpsertUserRegistry>['body']
export type UpsertUserResponse               = InferResponse<typeof UpsertUserRegistry>
export type ResendInvitationRequest          = InferRequest<typeof ResendInvitationRegistry>['body']
export type ResendInvitationResponse         = InferResponse<typeof ResendInvitationRegistry>
export type SetUserStatusRequest             = InferRequest<typeof SetUserStatusRegistry>['body']
export type SetUserStatusResponse            = InferResponse<typeof SetUserStatusRegistry>
export type FetchUserProfilePictureRequest   = InferRequest<typeof FetchUserProfilePictureRegistry>['body']
export type FetchUserProfilePictureResponse  = InferResponse<typeof FetchUserProfilePictureRegistry>
export type GetUsersResponse                 = InferResponse<typeof GetUsersRegistry>
export type GetUserResponse                  = InferResponse<typeof GetUserByNumberRegistry>
export type GetUserEventTimelineResponse     = InferResponse<typeof GetUserEventTimelineRegistry>
export type Invitation                       = InferResponse<typeof GetInvitationListRegistry>['content']['invitations'][number]
export type InvitationType                   = InferResponse<typeof GetInvitationTypesRegistry>['content']['invitationTypes'][number]
